// src/data/config.js
export const questionnaireData = {
  questionnaire: [
    {
      section_title: 'Relevancy',
      questions: [
        {
          id: '1',
          type: 'mcq',
          text: 'How relevant is this article in determining whether it is plausible that HMTV/MMTV-like virus can cause breast cancer in humans (relevant, somewhat relevant, or irrelevant)?',
          explanation:
            'A relevant article would fulfil either of the following criteria: 1) gives causal evidence for whether or not HMTV/MMTV-like virus or its species/family of microbes cause breast cancer or other cancers either in humans or animal models, or 2) contains a theoretical model for how HMTV/MMTV-like virus might cause breast cancer, irrespective of whether there is experimental evidence. A somewhat relevant article would fulfil either of the following criteria: 1) mentions both HMTV/MMTV-like virus and breast cancer, but does not give any causal arguments or evidence for HMTV/MMTV-like virus causing breast cancer, or 2) contains evidence examining whether other microbes cause breast cancer, if the findings can plausibly generalise to HMTV/MMTV-like virus and breast cancer. An irrelevant article would be a paper that does not fulfil the criteria for the relevant or somewhat relevant classifications.',
          options: ['Relevant', 'Somewhat relevant', 'Irrelevant'],
        },
        {
          id: '1.1',
          type: 'long_answer',
          text: 'Briefly explain why the paper is relevant, somewhat relevant, or irrelevant.',
        },
      ],
    },
    {
      section_title: 'Paper Integrity & Reliability',
      questions: [
        {
          id: '2',
          type: 'mcq',
          text: 'Determine how reliable this paper is and produce a paper reliability score out of 5.',
          options: [
            '5) No issues detected.',
            '4) Some noticeable issues, such as a few minor contradictions, or an identifiable (but not severe) conflict of interest.',
            '3) Integrity is reasonable but weakened by repeated minor contradictions, a few irrelevant citations, or questionable choices in data presentation.',
            '2) The paper contains major contradictions, numerous irrelevant references, and/or noticeable conflicts of interest that raise concerns about bias.',
            '1) The paper is entirely unreliable due to extensive issues, including irrelevant citations, extreme bias from major conflicts of interest, multiple major contradictions, and/or strong indications of data or image manipulation.',
          ],
        },
      ],
    },
    {
      section_title: 'Summary of Paper Contents',
      questions: [
        {
          id: '3',
          type: 'mcq',
          text: 'Based on the sample demographics and the country in which this study was performed, are the findings of this study applicable to African populations in general?',
          options: [
            'Yes, the findings are applicable as populations across Africa share similar genetic, socioeconomic, and/or environmental conditions.',
            'Partially applicable, but caution should be exercised due to differences in genetic, socioeconomic, and/or environmental conditions.',
            'No, the findings cannot be applied to African populations due to significant contextual differences.',
            'Applicability cannot be determined without further information about the study’s sample.',
          ],
        },
        {
          id: '4',
          type: 'long_answer',
          text: 'List the most important factor(s) in the methodology that influence(s) the strength of the study’s findings.',
        },
      ],
    },
    {
      section_title: 'Strength of Evidence',
      questions: [
        {
          id: '5',
          type: 'mcq',
          text: 'If applicable, was the sample size for cases - and, where relevant, controls - sufficient to support the paper’s conclusions?',
          options: [
            'Yes, the sample size was sufficient.',
            'The sample size was smaller than ideal, yet sufficiently large that, combined with strong statistical findings, the conclusions remain credible.',
            'No, the sample size was insufficient to support the conclusions.',
            'N/A, no sample used in this study.',
          ],
        },
        {
          id: '6',
          type: 'mcq',
          text: 'Are there any limitations to using this research as evidence for or against a plausible causative relationship between HMTV/MMTV-like virus and breast cancer?',
          options: [
            'Yes, there are limitations.',
            'No, there are no limitations.',
          ],
        },
        {
          id: '6.1',
          type: 'long_answer',
          text: 'If there are limitations, what are they?',
        },
        {
          id: '7',
          type: 'mcq',
          text: 'Assess the strength of evidence provided by this paper and assign a score from 1 to 5, with 1 representing severely weak / no credible evidence and 5 representing very strong evidence.',
          options: [
            '5) Very strong evidence',
            '4) Strong evidence',
            '3) Acceptable evidence',
            '2) Weak evidence',
            '1) Severely weak evidence/no credible evidence',
          ],
        },
      ],
    },
    {
      section_title:
        'Does this paper help fulfil or disprove the MCP causality criteria?',
      section_description:
        'Determine if this paper helps fulfil the following listed 10 microbial oncogenicity criteria for the MMTV-like virus/HMTV and breast cancer pair. Give 1 point for each criterion the paper supports or clearly fulfils, 0 for a criterion that is either not examined by the paper or remains uncertain at the end of the paper, and -1 for any criterion that is disproved by this paper. Briefly explain the reasoning for each point or lack thereof given. Finally, determine the cumulative score from these criteria points, allocated as the causal criteria score. When answering these questions, consider the paper [[reliability score|2]] and the [[strength of evidence score|7]].',
      questions: [
        {
          id: '8.1',
          type: 'group',
          title: 'Epidemiologic Association',
          description:
            'Consistent association of a microbe (or a combination of microbes) with a specific cancer type within a population (considering demographics and host factors) of humans (or across all populations), especially when compared to people within the same population(s) without cancer.',
          context:
            'Tools to consider:\n- Case-control and Cohort studies\n- Histopathology, immunochemistry, PCR\n- Genomics and mass spectrometry\n- Strong association when consistent findings across different geographical regions and consistent risk ratios in case-control or cohort studies',
          sub_questions: [
            {
              id: '8.1.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.1.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.1.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.1.4',
              type: 'long_answer',
              text: 'State which kind of evidence is provided according to the tools to consider categories and identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.2',
          type: 'group',
          title: 'Histopathologic association',
          description:
            'Consistent detection of the microbe (virus, bacterium, fungus, or parasite) in cancer tissues compared to healthy controls',
          context:
            'Tools to consider:\n- In Vitro assays (PCR, FACS, imaging)\n- Histopathology, immunochemistry, PCR\n- Genomics and mass spectrometry',
          sub_questions: [
            {
              id: '8.2.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.2.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.2.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.2.4',
              type: 'long_answer',
              text: 'State which kind of evidence is provided according to the tools to consider categories and identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.3',
          type: 'group',
          title: 'Temporal association',
          description: 'Evidence that infection precedes onset of cancer',
          context: 'Tools to consider:\n- Longitudinal (long term) studies',
          sub_questions: [
            {
              id: '8.3.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.3.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.3.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.3.4',
              type: 'long_answer',
              text: 'Identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.4',
          type: 'group',
          title: 'Experimental evidence of facilitation of oncogenesis',
          description:
            'Induction of cancer/precancerous changes upon introduction of the isolated microbe into appropriate models. Does oncogenic cellular transformation occur when a microbe is introduced into an animal (ideally a primate or other human-representative) model or tissue model?',
          context:
            'Tools to consider:\n- 3D biosystems (organoids) or animal models',
          sub_questions: [
            {
              id: '8.4.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.4.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.4.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.4.4',
              type: 'long_answer',
              text: 'Identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.5',
          type: 'group',
          title: 'Molecular and Multi-omics evidence for interaction',
          description:
            'i. Epigenetics (e.g. DNA methylation changes in H. pylori or HPV E6 oncogene resulting in P53 degradation), as well as mutational signatures\nii. Mutation effects (stimulating cellular mutations promoting cancer)\niii. Stimulating immune evasion mechanisms reducing immunosurveillance for emerging cancer cells\niv. Integration of microbial DNA into host genome',
          context:
            'Tools to consider:\n- in vitro assays, organoids, multi-omics assessments including:\n  - Molecular Evidence\n    - For bacteria: Identification of toxins, effector proteins, or metabolites that alter cellular signalling\n    - For viruses: Characterization of viral oncoproteins or insertional mutagenesis\n    - For fungi: Demonstration of mycotoxins or immune-modulating molecules with carcinogenic potential\n    - For parasites: Identification of chronic inflammatory responses or direct tissue damage mechanisms\n    - Evidence of microbial interference with DNA repair, cell cycle regulation, apoptosis, or immune surveillance\n  - Genomic evidence:\n    - Microbial DNA sequences or integration sites in tumor genome\n    - Host genetic susceptibility factors that enhance oncogenic potential of specific microbes, including demonstration of how microbial exposure can alter (or promote) known host genetic risk factors for cancer.\n    - Demonstratable facilitation of cancer-associated mutational signatures.\n  - Transcriptomic evidence:\n    - Expression of microbial genes or altered host gene expression profiles\n  - Proteomic evidence:\n    - Detection of microbial proteins or altered host protein responses\n  - Metabolomic evidence:\n    - Microbial metabolites or altered host metabolic pathways\n  - Microbiome analyses:\n    - Consistent dysbiosis patterns associated with specific cancers',
          sub_questions: [
            {
              id: '8.5.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.5.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.5.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.5.4',
              type: 'long_answer',
              text: 'State which kind of evidence is provided according to the tools to consider categories and identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.6',
          type: 'group',
          title: 'Prevention',
          description:
            'Does preventing the infection (or removing exposure to the microbe) reduce cancer or evidence of oncogenesis',
          context:
            'Tools to consider:\n- Clinical trials\n- Relevant animal models\n- For viruses: Reduced cancer incidence following vaccination (e.g., HPV, HBV)\n- For bacteria: Cancer prevention through antibiotic treatment or bacterial elimination\n- For fungi: Antifungal intervention effects on precancerous lesions\n- For parasites: Impact of antiparasitic treatment on cancer development\n- Prevention trials showing reduced cancer incidence after targeting the microbe',
          sub_questions: [
            {
              id: '8.6.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.6.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.6.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.6.4',
              type: 'long_answer',
              text: 'State which kind of evidence is provided according to the tools to consider categories and identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.7',
          type: 'group',
          title: 'Reproducibility and Validation',
          description:
            'Confirmation across independent laboratories, optimally using varied methodologies or models. Replication in diverse human populations and geographic settings. Concordance between in vitro, animal, and human studies',
          context: null,
          sub_questions: [
            {
              id: '8.7.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.7.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.7.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.7.4',
              type: 'long_answer',
              text: 'Identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.8',
          type: 'group',
          title: 'Dose Response relationship',
          description:
            'Relationship of severity or chronicity of the presumed offending infection with cancer initiation and severity in human longitudinal studies or in experimental models demonstrate the association of infectious dose with development of cancer',
          context:
            'Tools to consider:\n- Organoids, animal models, or clinico-epidemiologic longitudinal studies',
          sub_questions: [
            {
              id: '8.8.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.8.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.8.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.8.4',
              type: 'long_answer',
              text: 'State which kind of evidence is provided according to the tools to consider categories and identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.9',
          type: 'group',
          title: 'Plausibility',
          description:
            'Are there plausible mechanisms for considering a potential role for a microbe',
          context:
            'Tools to consider:\n- Knowledge-based; i.e. understanding the physiology of microbial interaction with humans may suggest or support a role in carcinogenesis',
          sub_questions: [
            {
              id: '8.9.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not discuss, or leaves this criterion uncertain.',
              options: [
                '0, the paper does not suggest or discuss any plausible mechanisms for the MCP.',
                '0, the paper suggests a possible mechanism, but it is weakly justified, inconsistent with existing knowledge, or supported by insufficient evidence from the study.',
                '1, the paper presents a plausible mechanism, either consistent with existing knowledge of cancer biology or microbial pathogenesis or supported by credible evidence produced by the present study.',
                '-1, the paper presents reasoning or mechanisms that argue against a plausible role for the microbe in carcinogenesis.',
              ],
            },
            {
              id: '8.9.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.9.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.9.4',
              type: 'long_answer',
              text: 'Identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '8.10',
          type: 'group',
          title: 'Impact of co-Factors',
          description:
            'Microbial carcinogenesis occurs (or is accelerated) in an environment with promotive host factors, such as genetic risk, immunodeficiencies, nutritional status, and/or with environmental factors (including but not limited to known carcinogens)',
          context:
            'Tools to consider:\n- Epidemiological studies in humans\n- Laboratory studies\n- Animal models',
          sub_questions: [
            {
              id: '8.10.1',
              type: 'mcq',
              text: 'Indicate whether the paper supports, refutes, does not investigate, or leaves this criterion uncertain.',
              options: [
                '0, the paper did not examine this criterion.',
                '0, the paper did investigate this criterion, but the findings are either inconclusive or uncertainty remains due to concerns about the study’s reliability or strength of evidence.',
                '1, the paper provides evidence that supports this criterion.',
                '-1, the paper provides evidence that refutes this criterion.',
              ],
            },
            {
              id: '8.10.2',
              type: 'multi_select_mcq',
              text: 'If this criterion was supported, refuted, or remains uncertain, select all applicable explanations below that describe why.',
              options: [
                'Insufficient power',
                'Inappropriate methodology/tools',
                'Concerns regarding paper reliability',
                'Error/s in the statistical analysis',
                'Sufficient power',
                'Appropriate methodology/tools',
                'Other (specify)',
              ],
            },
            {
              id: '8.10.3',
              type: 'long_answer',
              text: 'If you selected other, please specify.',
            },
            {
              id: '8.10.4',
              type: 'long_answer',
              text: 'State which kind of evidence is provided according to the tools to consider categories and identify the specific finding/s that fulfil or refute this criterion.',
            },
          ],
        },
        {
          id: '9.',
          type: 'mcq',
          text: 'Indicate whether the paper supports, refutes, or does not impact the plausibility of HMTV/MMTV-like virus causing breast cancer.',
          options: [
            '5) Strongly supports (moderate to strong evidence)',
            '4) Weakly supports (weak to moderate evidence)',
            '3) Does not impact (negligible or weak evidence)',
            '2) Weakly refutes (weak to moderate evidence)',
            '1) Strongly refutes (moderate to strong evidence)',
          ],
        },
      ],
    },
  ],
};

/**
 * Flattens the nested questionnaire structure into a single array of question objects.
 * This is used by other components for progress calculation and validation.
 * @param {string | number} paperId - The ID of the paper (currently unused but kept for API consistency).
 * @returns {Array<Object>} A flat array of all question objects.
 */
export const getQuestionsForPaper = (paperId) => {
  const allQuestions = [];

  const processNode = (node, section) => {
    // 1. Create a version of the question with its category
    const questionData = { ...node, category: section.section_title };

    // 2. Add the question itself to the flattened list
    allQuestions.push(questionData);

    // 3. If the question is a group, recursively process its sub-questions
    if (node.type === 'group' && node.sub_questions) {
      node.sub_questions.forEach((sub) => processNode(sub, section));
    }
  };

  // Iterate through each section and its top-level questions to start the process
  questionnaireData.questionnaire.forEach((section) => {
    if (section.questions && Array.isArray(section.questions)) {
      section.questions.forEach((q) => processNode(q, section));
    }
  });

  return allQuestions;
};

/**
 * Checks if a single question has a valid response.
 * @param {string} questionId - The ID of the question.
 * @param {Object} responses - The complete response object.
 * @returns {boolean} - True if the question is answered.
 */
export const isQuestionAnswered = (questionId, responses) => {
  const response = responses[questionId];
  return (
    response !== undefined &&
    response !== null &&
    (Array.isArray(response)
      ? response.length > 0
      : String(response).trim() !== '')
  );
};

/**
 * Determines if a question should be visible based on the current answers.
 * @param {Object} question - The question object to check.
 * @param {Object} responses - The complete response object.
 * @returns {boolean} - True if the question should be visible.
 */
export const isQuestionVisible = (question, responses) => {
  const { id } = question;
  const idParts = id.split('.');

  // Rule for question 6.1
  if (id === '6.1') {
    return responses['6'] === 'Yes, there are limitations.';
  }

  // Rules for section 8.x sub-questions
  if (id.startsWith('8.') && idParts.length === 3) {
    const baseMcqId = `${idParts[0]}.${idParts[1]}.1`;
    const baseMcqAnswer = responses[baseMcqId];
    const subQuestionSuffix = idParts[2];

    if (subQuestionSuffix === '1') {
      return true;
    }
    if (!baseMcqAnswer) {
      return false;
    }

    switch (subQuestionSuffix) {
      case '2':
        return baseMcqAnswer !== '0, the paper did not examine this criterion.';
      case '3':
        const multiSelectId = `${idParts[0]}.${idParts[1]}.2`;
        const multiSelectAnswers = responses[multiSelectId] || [];
        return multiSelectAnswers.includes('Other (specify)');
      case '4':
        return (
          baseMcqAnswer.startsWith('1,') || baseMcqAnswer.startsWith('-1,')
        );
      default:
        return true;
    }
  }

  // Default to visible
  return true;
};

/**
 * Calculates the completion progress for a given paper based on visible questions.
 * @param {string | number} paperId - The ID of the paper.
 * @param {Object} responses - The user's responses for that paper.
 * @returns {{answered: number, total: number, percentage: number}} - Progress object.
 */
export const calculatePaperProgress = (paperId, responses) => {
  const allQuestions = getQuestionsForPaper(paperId);
  // We only care about non-group questions for progress calculation
  const nonGroupQuestions = allQuestions.filter((q) => q.type !== 'group');

  // Filter down to only the questions that should be visible
  const visibleQuestions = nonGroupQuestions.filter((q) =>
    isQuestionVisible(q, responses)
  );

  // Count how many of the visible questions have been answered
  const answeredCount = visibleQuestions.filter((q) =>
    isQuestionAnswered(q.id, responses)
  ).length;

  const total = visibleQuestions.length;
  const percentage = total > 0 ? (answeredCount / total) * 100 : 0;

  return {
    answered: answeredCount,
    total,
    percentage,
  };
};
